import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import {
  Plane, Hotel, Search, Calendar, Users, ArrowRight,
  Clock, Star, MapPin, Wifi, Car, Coffee, Check,
  ExternalLink, Bookmark, Loader2, Globe
} from "lucide-react";

// Currency configuration with fixed exchange rates
const CURRENCIES = {
  USD: { symbol: "$", rate: 1, name: "US Dollar" },
  EUR: { symbol: "€", rate: 0.92, name: "Euro" },
  GBP: { symbol: "£", rate: 0.79, name: "British Pound" },
  CAD: { symbol: "CA$", rate: 1.36, name: "Canadian Dollar" },
  AUD: { symbol: "A$", rate: 1.53, name: "Australian Dollar" },
  JMD: { symbol: "J$", rate: 156.50, name: "Jamaican Dollar" },
};

export const TravelSearch = ({ isOpen, onClose, bucketItem }) => {
  const [activeTab, setActiveTab] = useState("flights");
  const [loading, setLoading] = useState(false);
  const [flightResults, setFlightResults] = useState(null);
  const [hotelResults, setHotelResults] = useState(null);
  const [currency, setCurrency] = useState("USD");
  
  // Load user's currency preference
  useEffect(() => {
    if (isOpen) {
      loadCurrency();
    }
  }, [isOpen]);

  const loadCurrency = async () => {
    try {
      const res = await axios.get(`${API}/user/currency`);
      if (res.data.currency) {
        setCurrency(res.data.currency);
      }
    } catch (error) {
      // Use default USD if not logged in or error - expected for guest users
      console.debug("Currency fetch failed, using default:", error.message);
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    setCurrency(newCurrency);
    try {
      await axios.put(`${API}/user/currency`, { currency: newCurrency });
    } catch (error) {
      // Silently fail for non-logged in users - expected behavior
      console.debug("Currency save failed:", error.message);
    }
  };

  const formatPrice = (amount) => {
    const { rate, symbol } = CURRENCIES[currency];
    const converted = (amount || 0) * rate;
    if (currency === "JMD") {
      return `${symbol}${Math.round(converted).toLocaleString()}`;
    }
    return `${symbol}${converted.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };
  
  // Flight search form
  const [flightForm, setFlightForm] = useState({
    origin: "",
    destination: bucketItem?.location || "",
    departure_date: "",
    return_date: "",
    passengers: 2
  });
  
  // Hotel search form
  const [hotelForm, setHotelForm] = useState({
    destination: bucketItem?.location || bucketItem?.country || "",
    check_in: "",
    check_out: "",
    guests: 2,
    rooms: 1
  });

  const searchFlights = async (e) => {
    e.preventDefault();
    if (!flightForm.origin || !flightForm.destination || !flightForm.departure_date) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        origin: flightForm.origin.toUpperCase(),
        destination: flightForm.destination.toUpperCase(),
        departure_date: flightForm.departure_date,
        passengers: flightForm.passengers.toString()
      });
      if (flightForm.return_date) {
        params.append("return_date", flightForm.return_date);
      }
      
      const res = await axios.get(`${API}/travel/search/flights?${params}`);
      setFlightResults(res.data);
    } catch (error) {
      toast.error("Failed to search flights");
    } finally {
      setLoading(false);
    }
  };

  const searchHotels = async (e) => {
    e.preventDefault();
    if (!hotelForm.destination || !hotelForm.check_in || !hotelForm.check_out) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        destination: hotelForm.destination,
        check_in: hotelForm.check_in,
        check_out: hotelForm.check_out,
        guests: hotelForm.guests.toString(),
        rooms: hotelForm.rooms.toString()
      });
      
      const res = await axios.get(`${API}/travel/search/hotels?${params}`);
      setHotelResults(res.data);
    } catch (error) {
      toast.error("Failed to search hotels");
    } finally {
      setLoading(false);
    }
  };

  const saveSearch = async (type, data) => {
    if (!bucketItem) {
      toast.error("No bucket item selected");
      return;
    }
    
    try {
      await axios.post(`${API}/travel/save-search?item_id=${bucketItem.id}&search_type=${type}`, {
        search_data: data
      });
      toast.success("Search saved to your trip!");
    } catch (error) {
      toast.error("Failed to save search");
    }
  };

  const openAffiliateLink = async (type) => {
    try {
      let url;
      if (type === "flights") {
        const res = await axios.get(`${API}/affiliates/flight-link`, {
          params: {
            origin: flightForm.origin.toUpperCase(),
            destination: flightForm.destination.toUpperCase(),
            departure_date: flightForm.departure_date,
            return_date: flightForm.return_date || undefined
          }
        });
        url = res.data.url;
        toast.success(`Opening ${res.data.affiliate}...`);
      } else if (type === "hotels") {
        const res = await axios.get(`${API}/affiliates/hotel-link`, {
          params: {
            destination: hotelForm.destination,
            check_in: hotelForm.check_in,
            check_out: hotelForm.check_out,
            guests: hotelForm.guests
          }
        });
        url = res.data.url;
        toast.success(`Opening ${res.data.affiliate}...`);
      }
      
      if (url) {
        window.open(url, "_blank");
      }
    } catch (error) {
      toast.error("Failed to generate booking link");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-card border-none rounded-2xl p-0 max-h-[90vh] overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
              <Plane className="w-5 h-5 text-primary" />
              Travel Search
            </DialogTitle>
            
            {/* Currency Selector */}
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <Select value={currency} onValueChange={handleCurrencyChange}>
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CURRENCIES).map(([code, { name, symbol }]) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{symbol}</span>
                        <span>{code}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogDescription className="text-muted-foreground">
            {bucketItem ? `Search flights and hotels for "${bucketItem.title}"` : "Search for flights and hotels"}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-6 border-b border-charcoal/5">
            <TabsList className="bg-transparent gap-4 h-12">
              <TabsTrigger 
                value="flights"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-6"
              >
                <Plane className="w-4 h-4 mr-2" />
                Flights
              </TabsTrigger>
              <TabsTrigger 
                value="hotels"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-6"
              >
                <Hotel className="w-4 h-4 mr-2" />
                Hotels
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="max-h-[70vh]">
            {/* Flights Tab */}
            <TabsContent value="flights" className="m-0">
              <div className="p-6">
                {/* Search Form */}
                <form onSubmit={searchFlights} className="bg-background rounded-xl p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">From</Label>
                      <Input
                        placeholder="JFK"
                        value={flightForm.origin}
                        onChange={(e) => setFlightForm({...flightForm, origin: e.target.value})}
                        className="uppercase font-mono"
                        maxLength={3}
                        data-testid="flight-origin"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">To</Label>
                      <Input
                        placeholder="LAX"
                        value={flightForm.destination}
                        onChange={(e) => setFlightForm({...flightForm, destination: e.target.value})}
                        className="uppercase font-mono"
                        maxLength={3}
                        data-testid="flight-destination"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Departure</Label>
                      <Input
                        type="date"
                        value={flightForm.departure_date}
                        onChange={(e) => setFlightForm({...flightForm, departure_date: e.target.value})}
                        data-testid="flight-departure"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Return</Label>
                      <Input
                        type="date"
                        value={flightForm.return_date}
                        onChange={(e) => setFlightForm({...flightForm, return_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Travelers</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={9}
                          value={flightForm.passengers}
                          onChange={(e) => setFlightForm({...flightForm, passengers: parseInt(e.target.value) || 1})}
                          className="w-16"
                        />
                        <Button 
                          type="submit" 
                          className="flex-1 bg-primary text-white hover:bg-primary/90"
                          disabled={loading}
                          data-testid="search-flights-btn"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>

                {/* Results */}
                {flightResults && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-charcoal">
                        {flightResults.total_results} flights found
                      </h3>
                      <Button
                        onClick={() => openAffiliateLink("flights")}
                        className="bg-primary text-white hover:bg-primary/90 text-sm"
                        data-testid="book-flights-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Book on Skyscanner
                      </Button>
                    </div>
                    
                    {flightResults.flights.map((flight) => (
                      <div
                        key={flight.id}
                        className="p-4 bg-background rounded-xl border border-charcoal/5 hover:shadow-md transition-shadow"
                        data-testid={`flight-${flight.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* Airline */}
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Plane className="w-6 h-6 text-primary" />
                            </div>
                            
                            {/* Flight details */}
                            <div>
                              <p className="font-medium text-charcoal">
                                {flight.airline} • {flight.flight_number}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>{flight.departure_time}</span>
                                <ArrowRight className="w-4 h-4" />
                                <span>{flight.arrival_time}</span>
                                <span className="text-xs">({flight.duration})</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Price and actions */}
                          <div className="text-right">
                            <p className="text-xl font-medium text-charcoal">
                              {formatPrice(flight.price)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatPrice(flight.price_per_person)}/person
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                flight.stops === 0 ? "bg-secondary/20 text-secondary" : "bg-muted text-muted-foreground"
                              }`}>
                                {flight.stops === 0 ? "Nonstop" : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                              </span>
                              {bucketItem && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => saveSearch("flight", flight)}
                                  className="h-7 w-7"
                                >
                                  <Bookmark className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Hotels Tab */}
            <TabsContent value="hotels" className="m-0">
              <div className="p-6">
                {/* Search Form */}
                <form onSubmit={searchHotels} className="bg-background rounded-xl p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-xs text-muted-foreground">Destination</Label>
                      <Input
                        placeholder="City or hotel name"
                        value={hotelForm.destination}
                        onChange={(e) => setHotelForm({...hotelForm, destination: e.target.value})}
                        data-testid="hotel-destination"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Check-in</Label>
                      <Input
                        type="date"
                        value={hotelForm.check_in}
                        onChange={(e) => setHotelForm({...hotelForm, check_in: e.target.value})}
                        data-testid="hotel-checkin"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Check-out</Label>
                      <Input
                        type="date"
                        value={hotelForm.check_out}
                        onChange={(e) => setHotelForm({...hotelForm, check_out: e.target.value})}
                        data-testid="hotel-checkout"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Guests</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          value={hotelForm.guests}
                          onChange={(e) => setHotelForm({...hotelForm, guests: parseInt(e.target.value) || 1})}
                          className="w-16"
                        />
                        <Button 
                          type="submit" 
                          className="flex-1 bg-primary text-white hover:bg-primary/90"
                          disabled={loading}
                          data-testid="search-hotels-btn"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>

                {/* Results */}
                {hotelResults && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-charcoal">
                        {hotelResults.total_results} hotels found
                      </h3>
                      <Button
                        onClick={() => openAffiliateLink("hotels")}
                        className="bg-secondary text-white hover:bg-secondary/90 text-sm"
                        data-testid="book-hotels-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Book on Booking.com
                      </Button>
                    </div>
                    
                    {hotelResults.hotels.map((hotel) => (
                      <div
                        key={hotel.id}
                        className="p-4 bg-background rounded-xl border border-charcoal/5 hover:shadow-md transition-shadow"
                        data-testid={`hotel-${hotel.id}`}
                      >
                        <div className="flex gap-4">
                          {/* Hotel image placeholder */}
                          <div className="w-32 h-24 rounded-lg bg-muted flex items-center justify-center shrink-0">
                            <Hotel className="w-8 h-8 text-muted-foreground/40" />
                          </div>
                          
                          {/* Hotel details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-charcoal">{hotel.name}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex items-center">
                                    {[...Array(hotel.star_rating)].map((_, i) => (
                                      <Star key={`star-${hotel.id}-${i}`} className="w-3 h-3 fill-accent text-accent" />
                                    ))}
                                  </div>
                                  <span className="text-sm text-muted-foreground">
                                    {hotel.guest_rating} ({hotel.review_count} reviews)
                                  </span>
                                </div>
                              </div>
                              
                              <div className="text-right">
                                <p className="text-xl font-medium text-charcoal">
                                  {formatPrice(hotel.price_per_night)}
                                </p>
                                <p className="text-xs text-muted-foreground">per night</p>
                              </div>
                            </div>
                            
                            {/* Amenities */}
                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                              {hotel.free_cancellation && (
                                <span className="text-xs px-2 py-0.5 rounded bg-secondary/20 text-secondary flex items-center gap-1">
                                  <Check className="w-3 h-3" />
                                  Free cancellation
                                </span>
                              )}
                              {hotel.breakfast_included && (
                                <span className="text-xs px-2 py-0.5 rounded bg-accent/20 text-foreground flex items-center gap-1">
                                  <Coffee className="w-3 h-3" />
                                  Breakfast included
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {hotel.distance_to_center} to center
                              </span>
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex items-center gap-2 mt-3">
                              {hotel.amenities.slice(0, 4).map((amenity) => (
                                <span key={amenity} className="text-xs text-muted-foreground">
                                  {amenity}
                                </span>
                              ))}
                              {bucketItem && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => saveSearch("hotel", hotel)}
                                  className="ml-auto"
                                >
                                  <Bookmark className="w-4 h-4 mr-1" />
                                  Save
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default TravelSearch;
